import getLang from 'I18n/utils/getLang';
import unescapedText from 'I18n/utils/unescapedText';
import { useSearchParams } from 'marketplace-ui-common/hooks/useSearchParams';
import { useEffect, useMemo } from 'react';
import { useLocation, useParams } from 'quartz-routing/react-router';
import { GET_SEARCH_FILTER_CONFIG_QUERY as PUBLIC_GET_SEARCH_FILTER_CONFIG_QUERY } from '../../../__generated__/chirp/com/hubspot/marketplace/storefront/service/rpc/MarketplaceStorefrontPublicRpc.dfc.new';
import { GET_SEARCH_FILTER_CONFIG_QUERY } from '../../../__generated__/chirp/com/hubspot/marketplace/storefront/service/rpc/MarketplaceStorefrontRpc.dfc.new';
import { Paths } from '../../constants/paths';
import { useAppContextQuery } from '../../hooks/useAppContextQuery';
import { useGates } from '../../hooks/useGates';
import { useProductTypes } from '../../hooks/useProductTypeSearchParams';
export function useFilterConfigData({
  allProductTypes
} = {}) {
  const productTypes = useProductTypes();
  const includesWorkflowTemplates = productTypes.includes('WORKFLOW_TEMPLATE');
  return useAppContextQuery({
    app: GET_SEARCH_FILTER_CONFIG_QUERY,
    public: PUBLIC_GET_SEARCH_FILTER_CONFIG_QUERY,
    options: {
      variables: Object.assign({
        language: getLang(),
        includeWorkflowTemplates: includesWorkflowTemplates
      }, !allProductTypes && productTypes.length > 0 && {
        productTypes
      })
    }
  });
}

/**
 * Important: Unlike `useFilterConfigData`, `useFilterConfig` will **NOT**
   return filters that are not applicable to the current situation.

   For example: product-specific filters will not be returned if they don't
   match the current product type.
 */
export function useFilterConfig(options) {
  var _options$productTypes;
  const params = useSearchParams();
  const rawProductTypeParam = params.get('PRODUCT_TYPE');
  const urlProductTypes = useProductTypes();
  const productTypes = (_options$productTypes = options === null || options === void 0 ? void 0 : options.productTypes) !== null && _options$productTypes !== void 0 ? _options$productTypes : urlProductTypes;
  const isCollection = !!useParams().collectionSlug;
  const location = useLocation();
  const {
    workflowTemplatesPhase2
  } = useGates();
  const {
    data,
    loading,
    error
  } = useFilterConfigData();
  const config = useMemo(() => {
    var _data$result;
    if (!(data !== null && data !== void 0 && (_data$result = data.result) !== null && _data$result !== void 0 && _data$result.filterConfigs)) return {};
    const completeConfig = {};
    Object.values(data.result.filterConfigs).forEach(filter => {
      var _filter$options, _filter$placeholder, _filter$defaultValue, _filter$optionGroups;
      let filterProductTypes = filter.productTypes;
      if (filter.field === 'PRODUCT_TYPE' && workflowTemplatesPhase2) {
        filterProductTypes = [...filterProductTypes, 'WORKFLOW_TEMPLATE'];
      }

      // ensure overlap between the filter's product types and the applicable product types
      if (!filterProductTypes.some(type => productTypes.includes(type))) {
        return;
      }

      // On the "All" search tab, hide all filters
      if (location.pathname === Paths.SEARCH_RESULTS && !rawProductTypeParam) {
        return;
      }

      // If this is a collection, hide the product type filter
      if (filter.field === 'PRODUCT_TYPE' && isCollection) return;
      if (filter.field === 'PRODUCT_TYPE' && location.pathname === Paths.SEARCH_RESULTS_SOLUTIONS_PARTNERS) {
        return;
      }
      let filterOptions = (_filter$options = filter.options) !== null && _filter$options !== void 0 ? _filter$options : [];
      if (filter.field === 'PRODUCT_TYPE' && !workflowTemplatesPhase2) {
        filterOptions = filterOptions.filter(o => o.value !== 'WORKFLOW_TEMPLATE');
      }
      if (filter.field === 'PRODUCT_TYPE' && workflowTemplatesPhase2) {
        const hasWorkflowTemplate = filterOptions.some(o => o.value === 'WORKFLOW_TEMPLATE');
        if (!hasWorkflowTemplate) {
          filterOptions = [...filterOptions, {
            text: unescapedText('marketplaceStorefront.filters.productType.workflowTemplates'),
            value: 'WORKFLOW_TEMPLATE'
          }];
        }
      }
      completeConfig[filter.field] = {
        field: filter.field,
        dataType: filter.dataType,
        hasMoreOptions: !!filter.hasMoreOptions,
        isMulti: filter.isMulti,
        label: filter.label,
        placeholder: (_filter$placeholder = filter.placeholder) !== null && _filter$placeholder !== void 0 ? _filter$placeholder : undefined,
        defaultValue: (_filter$defaultValue = filter.defaultValue) !== null && _filter$defaultValue !== void 0 ? _filter$defaultValue : undefined,
        productType: filter.productTypes.length === 1 ? filter.productTypes[0] : undefined,
        options: filterOptions,
        optionGroups: (_filter$optionGroups = filter.optionGroups) !== null && _filter$optionGroups !== void 0 ? _filter$optionGroups : []
      };
    });
    return completeConfig;
  }, [data, isCollection, location.pathname, productTypes, rawProductTypeParam, workflowTemplatesPhase2]);
  return Object.assign(config, {
    error,
    loading
  });
}
export function useRemoveInapplicableFilters() {
  var _filterConfigDataResu;
  const filterConfigDataResult = useFilterConfigData({
    allProductTypes: true
  });
  const rawFilters = (_filterConfigDataResu = filterConfigDataResult.data) === null || _filterConfigDataResu === void 0 ? void 0 : _filterConfigDataResu.result.filterConfigs;
  const filterConfig = useFilterConfig();
  const params = useSearchParams();
  useEffect(() => {
    if (!rawFilters || filterConfig.loading || filterConfig.error) return;
    const allFilterFields = Object.keys(rawFilters);
    let changeMade = false;
    allFilterFields.forEach(filterField => {
      const filterApplied = params.get(filterField);
      const filterApplicable = (filterField in filterConfig);
      if (filterApplied && !filterApplicable) {
        params.delete(filterField, {
          historyMethod: 'replace',
          defer: true
        });
        changeMade = true;
      }
    });
    if (changeMade) params.updateHistory();
  }, [rawFilters, filterConfig, params]);
}